import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import _ from 'lodash';
import { columnDefs, defaultColDef } from './utils/definitions-config';
import CouponCreate from './CouponCreate';
import {
  createMultipleCoupons,
  createSingleCoupon,
  deleteMultipleCoupons,
  editMultipleCoupons,
  editSingleCoupon,
  fetchCoupons,
} from '../../services/apiService';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import DeleteCouponDialog from './Dialog/DeleteCouponDialog';
import { toast } from 'react-toastify';
import EditCouponDialog from './Dialog/EditCouponDialog';
import LoadingOverlay from '../../extras/LoadingOverlay';
import { PageHeader, TableCard } from '../../extras/PageLayout';

function CouponsTable() {
  const [coupons, setCoupons] = useState([]);
  const gridRef = useRef();
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState([]);

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const openEditDialog = () => {
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
  };

  const refreshCoupons = async () => {
    try {
      setLoading(true);
      const response = await fetchCoupons();
      setCoupons(response.data);
    } catch (e) {
      toast.error('Coupons could not be fetched');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCoupons();
  }, []);
  useEffect(() => {
    setSelectedCoupons([]);
    gridRef.current?.api?.deselectAll();
  }, [coupons]);

  const upsertCoupons = (newCoupons) => {
    const newAllCoupons = _.uniqWith([...newCoupons, ...coupons], (c1, c2) => {
      return (
        c1.goalType === c2.goalType &&
        c1.couponCode === c2.couponCode &&
        c1.planType === c2.planType &&
        c1.subPlanType === c2.subPlanType
      );
    });
    setCoupons(newAllCoupons);
  };

  const handleEditCoupon = async ({
    expiryDate,
    discountAmount,
    beforeSignupDate,
    validForUsers,
    owner,
  }) => {
    const couponsToEdit = selectedCoupons.map((coupon) => ({
      couponCode: coupon.couponCode,
      goalType: coupon.goalType,
      planType: coupon.planType,
      subPlanType: coupon.subPlanType,
      expiryDate,
      discountAmount,
      beforeSignupDate,
      validForUsers:
        selectedCoupons?.length === 1 && !validForUsers ? null : validForUsers,
      owner,
    }));
    if (couponsToEdit?.length === 1) {
      try {
        setLoading(true);
        const updatedCoupons = await editSingleCoupon(couponsToEdit[0]);
        if (updatedCoupons) {
          toast.success('Coupon updated successfully');
          upsertCoupons([updatedCoupons]);
        }
        closeEditDialog();
        return;
      } catch (e) {
        console.error('Failed with Error: ', e);
        toast.error('Failed updating coupons');
        return;
      } finally {
        setLoading(false);
      }
    }
    try {
      setLoading(true);
      const updatedCoupons = await editMultipleCoupons(couponsToEdit);
      if (updatedCoupons) {
        toast.success('Coupon updated successfully');
        upsertCoupons(updatedCoupons.processedItems);
      }
      closeEditDialog();
    } catch (e) {
      console.error('Failed with Error: ', e);
      toast.error('Failed updating coupons');
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupons = (couponsToDelete) => {
    const filteredCoupons = _.differenceWith(
      coupons,
      couponsToDelete,
      (c1, c2) =>
        c1.goalType === c2.goalType &&
        c1.couponCode === c2.couponCode &&
        c1.planType === c2.planType &&
        c1.subPlanType === c2.subPlanType
    );
    setCoupons(filteredCoupons);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const couponsToDelete = selectedCoupons;
      const deleteResponse = await deleteMultipleCoupons(couponsToDelete);
      toast.success(deleteResponse.message || 'Deletion successfull');
      deleteCoupons(couponsToDelete);
      closeDeleteDialog();
    } catch (e) {
      console.error('Failed with Error: ', e);
      toast.error('Error deleting coupons');
    } finally {
      setLoading(false);
    }
  };

  const onSelectionChanged = useCallback(
    (event) => {
      const rows = event.api.getSelectedNodes();
      const result = rows.map(({ data }) => data);
      setSelectedCoupons(result);
    },
    [setSelectedCoupons]
  );

  const handleSingleCouponCreation = async (coupon) => {
    try {
      setLoading(true);
      const response = await createSingleCoupon(coupon);
      upsertCoupons([response]);
      toast.success('Successfully created coupon');
    } catch (e) {
      console.error(e);
      toast.error('Error happened creating coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCouponCreation = async (coupons) => {
    try {
      setLoading(true);
      await createMultipleCoupons(coupons);
      await refreshCoupons();
      toast.success('Successfully created multiple coupons');
    } catch (e) {
      console.error(e);
      toast.error('Error occurred on bulk creating coupons');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='coupons-table'>
      {loading && <LoadingOverlay />}
      <PageHeader
        title="Coupons"
        subtitle="Manage all coupons"
        actions={
          <>
            <IconButton
              onClick={openEditDialog}
              disabled={selectedCoupons.length === 0}
              style={selectedCoupons.length === 0 ? {} : { color: 'var(--primary-main)' }}
              size='small'
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={openDeleteDialog}
              disabled={selectedCoupons.length === 0}
              style={selectedCoupons.length === 0 ? {} : { color: 'var(--primary-main)' }}
              size='small'
            >
              <DeleteIcon />
            </IconButton>
            <CouponCreate
              refreshCoupons={refreshCoupons}
              setData={setCoupons}
              data={coupons}
              onCreateButtonClicked={handleSingleCouponCreation}
              onBulkCreateButtonClicked={handleBulkCouponCreation}
            />
          </>
        }
      />
      <TableCard
        title="Coupons"
        count={coupons.length}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={coupons}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          domLayout="normal"
          rowHeight={52}
          headerHeight={40}
          pagination={true}
          paginationPageSize={20}
          rowSelection='multiple'
          onSelectionChanged={onSelectionChanged}
          animateRows={true}
          suppressCellFocus
        />
      </TableCard>

      {deleteDialogOpen && (
        <DeleteCouponDialog
          selectedCoupons={selectedCoupons}
          open={deleteDialogOpen}
          handleClose={closeDeleteDialog}
          onDeleteButtonClicked={handleDelete}
        />
      )}
      {/* --- Form Dialog --- */}
      {editDialogOpen && (
        <EditCouponDialog
          handleClose={closeEditDialog}
          selectedCoupons={selectedCoupons}
          open={editDialogOpen}
          onEditButtonClicked={handleEditCoupon}
        />
      )}
    </div>
  );
}

export default CouponsTable;
